import React from 'react';
import { isLifeBot } from './lifeObjectType';
import { StudioIcon } from '../design/StudioShell';
import { MediaItem } from '../experiences/LifeObjectRenderer';
import LifeObjectExperiencePreview from './LifeObjectExperiencePreview';
import StudioConversation from '../design/StudioConversation';
import LifeObjectRenderer from '../experiences/LifeObjectRenderer';
import '../design/Studio.css';

const signalLabels = {
  qr: 'LifeCode',
  geo: 'Location',
  'geo-point': 'Location',
  'geo-fence': 'Location area',
  audio: 'Audio',
  url: 'Web link',
  nfc: 'LifeTap',
  ble: 'Bluetooth',
  spatial: 'Spatial placement',
};
const signalIcons = {
  qr: 'qr',
  geo: 'place',
  'geo-point': 'place',
  'geo-fence': 'place',
  audio: 'audio',
  url: 'link',
  nfc: 'signals',
  ble: 'signals',
  spatial: 'spatial',
};

function Signal({ anchor }) {
  const coordinates = anchor.coordinates || {};
  const hasLocation =
    Number.isFinite(coordinates.latitude) &&
    Number.isFinite(coordinates.longitude);
  const label =
    anchor.label ||
    anchor.metadata?.address ||
    anchor.name ||
    signalLabels[anchor.type] ||
    'Connected signal';
  return (
    <div className="lt-detail-signal">
      <span className="lt-feature-icon">
        <StudioIcon name={signalIcons[anchor.type] || 'signals'} />
      </span>
      <div>
        <strong>{signalLabels[anchor.type] || anchor.type}</strong>
        <small>{label}</small>
        {anchor.radiusMeters && (
          <small>Within {anchor.radiusMeters} metres</small>
        )}
      </div>
      {hasLocation && (
        <a
          href={`https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}`}
          target="_blank"
          rel="noreferrer"
          aria-label="View placement on map"
        >
          Map ↗
        </a>
      )}
    </div>
  );
}

export default function LifeObjectPreviewOverlay({
  lifeObject,
  previewUrl,
  onClose,
  onEdit,
  onDuplicate,
  onDelete,
  saving,
}) {
  const [tab, setTab] = React.useState('content');
  const [experience, setExperience] = React.useState(false);
  if (!lifeObject) return null;
  if (experience)
    return (
      <LifeObjectExperiencePreview
        {...{ lifeObject, previewUrl, onEdit, onDuplicate, saving }}
        onClose={() => setExperience(false)}
        onExit={onClose}
      />
    );
  const audience = lifeObject.audience || {};
  const lifecycle = lifeObject.lifecycle || {};
  const start = lifecycle.startAt || lifecycle.startsAt;
  const end = lifecycle.endAt || lifecycle.endsAt;
  const date = (value) =>
    new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  return (
    <div className="lt-detail">
      <header className="lt-detail-header">
        <span className="lt-section-eyebrow">
          {isLifeBot(lifeObject) ? 'LIFEBOT' : 'LIFEOBJECT'} / DETAILS
        </span>
        <button
          className="lt-icon-button"
          aria-label="Close preview"
          onClick={onClose}
        >
          <StudioIcon name="close" />
        </button>
      </header>
      <div className="lt-detail-layout">
        <section className="lt-detail-primary">
          <span className={`lt-status ${lifecycle.status}`}>
            {lifecycle.status === 'active' ? 'Published' : lifecycle.status}
          </span>
          <h1>{lifeObject.name}</h1>
          {lifeObject.owner?.name && (
            <p className="lt-page-description">
              Created by {lifeObject.owner.name}
            </p>
          )}
          <div className="lt-detail-tabs">
            <button
              className={tab === 'content' ? 'active' : ''}
              onClick={() => setTab('content')}
            >
              The experience
            </button>
            <button
              className={tab === 'conversation' ? 'active' : ''}
              onClick={() => setTab('conversation')}
            >
              Conversation
            </button>
          </div>
          {tab === 'content' && isLifeBot(lifeObject) ? (
            lifeObject.chatbot.chatBotId &&
            lifeObject.chatbot.status !== 'processing' &&
            lifeObject.chatbot.status !== 'failed' ? (
              <LifeObjectRenderer lifeObject={lifeObject} />
            ) : (
              <p>
                Your LifeBot{' '}
                {lifeObject.chatbot.status === 'failed'
                  ? 'needs attention. Edit its sources to try again.'
                  : 'is being prepared. Return to its setup to check progress.'}
              </p>
            )
          ) : tab === 'content' ? (
            <div className="lt-detail-content">
              {(lifeObject.content || []).map((item, index) => (
                <section key={item.id || index}>
                  <div className="lt-section-eyebrow">
                    {String(index + 1).padStart(2, '0')} / {item.type}
                  </div>
                  <MediaItem item={item} />
                </section>
              ))}
              {!lifeObject.content?.length && (
                <p>
                  No content added yet. Edit this LifeObject to tell its story.
                </p>
              )}
            </div>
          ) : (
            <StudioConversation lifeObjectId={lifeObject.id} />
          )}
        </section>
        <aside className="lt-detail-aside">
          <span className="lt-section-eyebrow">THE RIGHT MOMENT</span>
          <h2>
            Made to be
            <br />
            <em>discovered.</em>
          </h2>
          <section>
            <h3>Who can discover it</h3>
            <p>
              {audience.visibility === 'public'
                ? 'Everyone'
                : audience.visibility === 'followers'
                ? 'Followers'
                : audience.identifiers?.length > 1
                ? 'Selected people'
                : 'Private audience'}
            </p>
            {audience.identifiers?.length > 0 && (
              <small>
                {audience.identifiers.length} selected recipient
                {audience.identifiers.length === 1 ? '' : 's'}
              </small>
            )}
          </section>
          <section>
            <h3>When it’s available</h3>
            <p>{start ? 'From ' + date(start) : 'No start date'}</p>
            <p>{end ? 'Until ' + date(end) : 'No end date'}</p>
            {lifecycle.recurrence && lifecycle.recurrence !== 'none' && (
              <small>Repeats: {lifecycle.recurrence}</small>
            )}
          </section>
          <section>
            <h3>Where discovery happens</h3>
            {lifeObject.anchors?.length ? (
              <>
                {lifeObject.anchors.map((anchor, i) => (
                  <Signal anchor={anchor} key={anchor.id || i} />
                ))}
                <small>Every connected condition must be met.</small>
              </>
            ) : (
              <p>No location or physical signal attached.</p>
            )}
          </section>
          <button className="lt-button" onClick={() => setExperience(true)}>
            Preview on a device <StudioIcon name="arrow" size={17} />
          </button>
          {previewUrl && (
            <a
              className="lt-detail-public"
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open contextual link ↗
            </a>
          )}
        </aside>
      </div>
      <footer className="lt-detail-footer">
        <button
          className="lt-detail-secondary"
          disabled={saving}
          onClick={() => onDuplicate?.(lifeObject)}
        >
          Duplicate LifeObject
        </button>
        {onDelete && (
          <button
            className="lt-button lt-button-quiet"
            onClick={() => onDelete(lifeObject)}
            disabled={saving}
          >
            Delete LifeObject
          </button>
        )}
        <button className="lt-button" onClick={() => onEdit(lifeObject)}>
          Edit LifeObject <StudioIcon name="arrow" size={17} />
        </button>
      </footer>
    </div>
  );
}
